// Use case-ikoner v3 — "Geometric whispers".
//
// Designspråk:
//   • viewBox 0 0 120 120 — bibehållen från v2 för layout-kompatibilitet
//   • Pure shape-language: rektanglar, cirklar, linjer. INGEN typografi inuti.
//   • INGEN crop-mark-inramning — generös negativ yta får ramen
//   • Hairline strokes (1px) som default, 1.5px för det element som ska
//     bära primärt visuellt fokus i kompositionen
//   • Per ikon: en eller två små solid cream-prickar/-rutor som "anchor" —
//     ger varje mark en tydlig viktpunkt utan att introducera figuration
//   • currentColor genomgående — färgas av .vl-usecase-icon (cream accent)
//   • Inspiration: SSENSE section dividers, The Gentlewoman feature markers,
//     Apartamento glyphs, architectural blueprints. Subtle, finstämt, inte
//     bokstavligt — antyder snarare än illustrerar.
//
// Användning i en use case-box:
//   <div className="vl-usecase-icon"><AgencySubmissionIcon /></div>

interface IconProps {
  className?: string;
}

function wrap(children: React.ReactNode, { className }: IconProps) {
  return (
    <svg
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ width: '100%', height: '100%', display: 'block', color: 'currentColor' }}
      aria-hidden
    >
      {children}
    </svg>
  );
}

/* ─── MODELS ─────────────────────────────────────────── */

// 1. Agency submission — a subject leaving a threshold along a flow line,
//    arriving at a destination ring. Suggests "sending out" without an envelope.
export const AgencySubmissionIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <line x1="18" y1="60" x2="86" y2="60" stroke="currentColor" strokeWidth={1} />
      <rect x="30" y="42" width="36" height="36" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.55} />
      <rect x="54" y="54" width="12" height="12" fill="currentColor" />
      <circle cx="98" cy="60" r="3.5" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="98" cy="60" r="1" fill="currentColor" />
    </>,
    p,
  );

// 2. Casting request — three offset rectangles stacked: same shape,
//    different framings. Suggests "tailored variants per audience".
export const CastingRequestIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <rect x="22" y="30" width="48" height="60" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.45} />
      <rect x="36" y="36" width="48" height="60" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.7} />
      <rect x="50" y="42" width="48" height="60" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="74" cy="66" r="2" fill="currentColor" />
    </>,
    p,
  );

// 3. Personal archive — horizontal strata, layers of time. The middle
//    layer is emphasized with a solid stroke and anchor dot — "the now".
export const PersonalArchiveIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <line x1="22" y1="32" x2="98" y2="32" stroke="currentColor" strokeWidth={1} opacity={0.45} />
      <line x1="22" y1="44" x2="98" y2="44" stroke="currentColor" strokeWidth={1} opacity={0.6} />
      <line x1="22" y1="56" x2="98" y2="56" stroke="currentColor" strokeWidth={1} opacity={0.75} />
      <line x1="22" y1="68" x2="98" y2="68" stroke="currentColor" strokeWidth={1.5} />
      <line x1="22" y1="80" x2="98" y2="80" stroke="currentColor" strokeWidth={1} opacity={0.75} />
      <line x1="22" y1="92" x2="98" y2="92" stroke="currentColor" strokeWidth={1} opacity={0.6} />
      <circle cx="60" cy="68" r="2.5" fill="currentColor" />
    </>,
    p,
  );

// 4. Brand pitch — a histogram-like row of varying bars suggesting
//    range and breadth. The tallest bar is solid — "your signature".
export const BrandPitchIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <rect x="20" y="56" width="10" height="38" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.6} />
      <rect x="36" y="38" width="10" height="56" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.75} />
      <rect x="52" y="26" width="10" height="68" fill="currentColor" />
      <rect x="68" y="44" width="10" height="50" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.75} />
      <rect x="84" y="50" width="10" height="44" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.6} />
      <line x1="16" y1="98" x2="98" y2="98" stroke="currentColor" strokeWidth={1} opacity={0.4} />
    </>,
    p,
  );

/* ─── PHOTOGRAPHERS ──────────────────────────────────── */

// 5. Client gallery, evolved — a 3×3 grid with two cells filled as selects.
//    Pure contact-sheet geometry, the act of selection becomes the subject.
export const ClientGalleryIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <g stroke="currentColor" strokeWidth={1} opacity={0.6} fill="none">
        <rect x="22" y="22" width="22" height="22" />
        <rect x="49" y="22" width="22" height="22" />
        <rect x="76" y="22" width="22" height="22" />
        <rect x="22" y="49" width="22" height="22" />
        <rect x="49" y="49" width="22" height="22" />
        <rect x="76" y="49" width="22" height="22" />
        <rect x="22" y="76" width="22" height="22" />
        <rect x="49" y="76" width="22" height="22" />
        <rect x="76" y="76" width="22" height="22" />
      </g>
      <rect x="49" y="22" width="22" height="22" fill="currentColor" />
      <rect x="22" y="76" width="22" height="22" fill="currentColor" />
    </>,
    p,
  );

// 6. Personal portfolio — concentric circles with a single arc-segment
//    suggesting circularity / perpetuum / a living document.
export const PersonalPortfolioIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <circle cx="60" cy="60" r="34" fill="none" stroke="currentColor" strokeWidth={1} />
      <circle cx="60" cy="60" r="22" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.55} />
      <path d="M60 22 A38 38 0 0 1 94 56" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
      <circle cx="60" cy="22" r="3" fill="currentColor" />
    </>,
    p,
  );

// 7. Talent collaboration — two parallel rectangles offset. Same shape
//    twice = master and copy, side by side.
export const TalentCollabIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <rect x="22" y="28" width="56" height="64" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.55} />
      <rect x="42" y="38" width="56" height="64" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <rect x="64" y="60" width="6" height="6" fill="currentColor" />
    </>,
    p,
  );

// 8. Submission packets — one large primary plate above, two smaller
//    secondaries beneath. Pure editorial hierarchy.
export const SubmissionPacketIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <rect x="22" y="20" width="76" height="50" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="34" cy="32" r="2.5" fill="currentColor" />
      <rect x="22" y="80" width="36" height="20" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.6} />
      <rect x="62" y="80" width="36" height="20" fill="none" stroke="currentColor" strokeWidth={1} opacity={0.6} />
    </>,
    p,
  );

/* ─── CREATIVE DUOS ──────────────────────────────────── */

// 9. Creator + partner — two overlapping circles (Venn-like) with two
//    anchor dots, one per identity. Shared workflow, distinct authorship.
export const CreatorPartnerIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <circle cx="46" cy="60" r="26" fill="none" stroke="currentColor" strokeWidth={1} />
      <circle cx="74" cy="60" r="26" fill="none" stroke="currentColor" strokeWidth={1} />
      <circle cx="34" cy="60" r="2.5" fill="currentColor" />
      <circle cx="86" cy="60" r="2.5" fill="currentColor" />
    </>,
    p,
  );

// 10. Two photographers — a single source splits into two parallel
//     streams. One filled anchor (the source), two open rings (selections).
export const TwoPhotographersIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <line x1="22" y1="60" x2="44" y2="60" stroke="currentColor" strokeWidth={1.5} />
      <line x1="44" y1="60" x2="60" y2="40" stroke="currentColor" strokeWidth={1} />
      <line x1="44" y1="60" x2="60" y2="80" stroke="currentColor" strokeWidth={1} />
      <line x1="60" y1="40" x2="96" y2="40" stroke="currentColor" strokeWidth={1} />
      <line x1="60" y1="80" x2="96" y2="80" stroke="currentColor" strokeWidth={1} />
      <circle cx="22" cy="60" r="3" fill="currentColor" />
      <circle cx="96" cy="40" r="3" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="96" cy="80" r="3" fill="none" stroke="currentColor" strokeWidth={1.5} />
    </>,
    p,
  );

// 11. Model + agent — a primary plate paired with an authority ring
//     in the corner. Curation + approval, hierarchy without illustration.
export const ModelAgentIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <rect x="20" y="32" width="56" height="62" fill="none" stroke="currentColor" strokeWidth={1} />
      <circle cx="34" cy="44" r="2" fill="currentColor" />
      <circle cx="86" cy="42" r="16" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="86" cy="42" r="3" fill="currentColor" />
    </>,
    p,
  );

// 12. Influencer + manager — a horizontal axis: solid square (you) hands
//     off via a small open marker to a destination ring (brand). Speed,
//     direction, handoff — all geometric.
export const InfluencerManagerIcon = (p: IconProps = {}) =>
  wrap(
    <>
      <line x1="20" y1="60" x2="92" y2="60" stroke="currentColor" strokeWidth={1} />
      <rect x="22" y="52" width="16" height="16" fill="currentColor" />
      <rect x="56" y="56" width="8" height="8" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="92" cy="60" r="7" fill="none" stroke="currentColor" strokeWidth={1.5} />
      <circle cx="92" cy="60" r="2" fill="currentColor" />
    </>,
    p,
  );
